import styles from './tui-favorites-notification.scss';
import { translate } from '../translations/index.js';

import { isMobileScreen } from '../utils/helper';

export const NotificationType = {
  SAVED: 'SAVED',
  SAVED_WITH_CHANGE_TRIGGER: 'SAVED_WITH_CHANGE_TRIGGER',
  REMOVED: 'REMOVED_FAVORITE'
};

class FavoritesNotification extends HTMLElement {
  #data = {
    giataId: '',
    listId: '',
    listName: '',
    placeholderListName: '',
    searchParams: ''
  };
  #locale = '';
  #notificationType = '';

  constructor() {
    super();
    this.#observe();
  }

  connectedCallback() {
    this.#locale = this.getAttribute('locale');

    if (isMobileScreen()) {
      this.#render();
      this.#setModalBaseStyles();
    }
  }

  /**
   * Show notificiation for the given duration with the given data.
   *
   * @param {NotificationType} notificationType
   * @param {Number} duration in milliseconds
   * @param {Object} data
   */
  show(notificationType, duration, data) {
    this.#data = data;
    this.#notificationType = notificationType;

    this.#render();

    // Set styles
    const notificationElement = this.querySelector('.tui-favorites-notification');

    notificationElement.style.bottom = '0';
    notificationElement.style.pointerEvents = 'all';

    setTimeout(() => {
      this.#close();
    }, duration);
  }

  #render() {
    const notificationElement = this.querySelector('.tui-favorites-notification');

    if (notificationElement) {
      notificationElement.innerHTML = `
        ${this.#notificationType === NotificationType.SAVED ? this.#renderSaved() : ''}
        ${this.#notificationType === NotificationType.SAVED_WITH_CHANGE_TRIGGER ? this.#renderSavedWithTrigger() : ''}
        ${this.#notificationType === NotificationType.REMOVED ? this.#renderRemoved() : ''}
      `;
    } else {
      this.innerHTML = `
        <style>${styles}</style>
        <div class="tui-favorites-notification">
          ${this.#notificationType === NotificationType.SAVED ? this.#renderSaved() : ''}
          ${this.#notificationType === NotificationType.SAVED_WITH_CHANGE_TRIGGER ? this.#renderSavedWithTrigger() : ''}
          ${this.#notificationType === NotificationType.REMOVED ? this.#renderRemoved() : ''}
        </div>`;
    }
  }

  #renderSaved() {
    return `
      <div class="tui-favorites-notification__content">
        <span>
          ${translate('favorites.notification.savedIn', {
            locale: this.#locale
          })} <button class="tui-favorites-notification-action tui-favorites-notification-action--clip-text" data-action="openList">${
      this.#data.listName
    }</button>
        </span>
      </div>`;
  }

  #renderSavedWithTrigger() {
    return `
      <div class="tui-favorites-notification__content tui-favorites-notification-with-trigger">
        <div class="tui-favorites-notification-with-trigger__column">
          <span class="tui-favorites-notification-action-label">${translate('favorites.notification.savedIn', {
            locale: this.#locale
          })}:</span>
          <button class="tui-favorites-notification-action tui-favorites-notification-action--clip-text" data-action="openList">${
            this.#data.listName
          }</button>
        </div>
        <div class="tui-favorites-notification-with-trigger__column">
          <button class="tui-favorites-notification-action" data-action="changeList">${translate(
            'favorites.notification.change',
            {
              locale: this.#locale
            }
          )}</button>
        </div>
      </div>`;
  }

  #renderRemoved() {
    return `
      <div class="tui-favorites-notification__content tui-favorites-notification-with-trigger">
        <div class="tui-favorites-notification-with-trigger__column">
          <span class="tui-favorites-notification-action-label">Entfernt aus:</span>
          <button class="tui-favorites-notification-action tui-favorites-notification-action--clip-text" data-action="openList">${
            this.#data.listName
          }</button>
        </div>
        <div class="tui-favorites-notification-with-trigger__column">
          <button class="tui-favorites-notification-action" data-action="revertDelete">${translate(
            'favorites.notification.undone',
            {
              locale: this.#locale
            }
          )}</button>
        </div>
      </div>`;
  }

  #observe() {
    this.addEventListener('click', (event) => {
      const { action } = event.target?.dataset || {};

      if (!action) return;

      switch (action) {
        case 'changeList':
          this.dispatchEvent(
            new CustomEvent('tui-favorites-popup.OpenPopup', {
              bubbles: true,
              composed: true,
              detail: {
                giataId: this.#data.giataId,
                listId: this.#data.listId,
                placeholderListName: this.#data.placeholderListName,
                searchParams: this.#data.searchParams
              }
            })
          );

          // Close modal when changing list
          this.#setModalBaseStyles();

          break;

        case 'openList':
          this.dispatchEvent(
            new CustomEvent('tui-favorites-popup.OpenFavoritesList', {
              bubbles: true,
              composed: true,
              detail: {
                listId: this.#data.listId,
                numberOfEntries: this.#data.numberOfListEntries || 0
              }
            })
          );

          break;

        case 'revertDelete':
          this.dispatchEvent(
            new CustomEvent('tui-favorites-popup.AddEntryToFavoriteList', {
              bubbles: true,
              composed: true,
              detail: {
                giataId: this.#data.giataId,
                listId: this.#data.listId,
                searchParams: this.#data.searchParams,
                showNotification: false
              }
            })
          );

          this.#close();

          break;
        default:
          console.warn(`Favorites notification action "${action}" unhandled`);
      }
    });
  }

  #close() {
    this.#notificationType = '';

    const notificationElement = this.querySelector('.tui-favorites-notification');

    // Reset styles
    notificationElement.style.bottom = '-80px';
    notificationElement.style.pointerEvents = 'none';
  }

  #setModalBaseStyles() {
    const notificationElement = this.querySelector('.tui-favorites-notification');

    notificationElement.style.position = 'fixed';
    notificationElement.style.bottom = '-80px';
    notificationElement.style.transition = 'bottom .5s ease-in-out';
    notificationElement.style.pointerEvents = 'none';
  }
}

customElements.define('tui-favorites-notification', FavoritesNotification);
